import produce from "immer"
import type { MapDetailDto } from "../../models/models"
import { remove, upload } from "../../services"
import {
  copyProject,
  createEmptyProjectFromMap,
  createProject,
  getProject,
  removeProject,
  updateProject
} from "../../services/ProjectService"
import { extractCloudinaryAssetId, isCloudinaryAsset } from "../../utils"
import { history } from "../Store"
import type { AppThunkAction, AppThunkDispatch } from "../app.types"
import { ATTACHMENT_EDITOR_UPDATE_PIN_POSITION } from "../attachment-editor/attachment-editor.actions"
import type { AttachmentEditorAction } from "../attachment-editor/attachment-editor.types"
import { fetchBosatlas } from "../bosatlas/bosatlas.actions"
import { AttachmentType } from "./../../models/models"
import { bosAtlasStore } from "./../Store"
import type {
  Attachment,
  IMapDrawing,
  Project,
  ProjectAction,
  ProjectActions,
  ProjectAttachementAction,
  ProjectAttachmentListAction,
  ProjectDrawAction,
  ProjectDrawingsActions
} from "./projects.types"

export const PROJECT_FETCH: string = "PROJECT_FETCH"
export const PROJECT_LOAD_FROM_MAP: string = "PROJECT_LOAD_FROM_MAP"
export const PROJECT_ADD_DRAWING: string = "PROJECT_ADD_DRAWING"
export const PROJECT_SYNC_POPUP_STATES: string = "PROJECT_SYNC_POPUP_STATES"
export const PROJECT_REMOVE_DRAWING: string = "PROJECT_REMOVE_DRAWING"

export const PROJECT_EDIT_DRAWING_COLOR: string = "PROJECT_EDIT_DRAWING_COLOR"
export const PROJECT_EDIT_DRAWING_WEIGHT: string = "PROJECT_EDIT_DRAWING_WEIGHT"
export const PROJECT_MOVE_DRAWING: string = "PROJECT_EDIT_DRAWING_POSITION"
export const PROJECT_EDIT_DRAWING_POPUP: string = "PROJECT_EDIT_DRAWING_POPUP"
export const PROJECT_CLOSE_DRAWING_POPUP = "PROJECT_CLOSE_DRAWING_POPUP"
export const PROJECT_ADD_POINT_TO_POLYLINE: string = "PROJECT_ADD_POINT_TO_POLYLINE"
export const PROJECT_EDIT_DRAWING_TEXT: string = "PROJECT_EDIT_DRAWING_TEXT"

export const PROJECT_CLEAR: string = "PROJECT_CLEAR"
export const PROJECT_CREATE: string = "PROJECT_CREATE"
export const PROJECT_UPDATE: string = "PROJECT_UPDATE"
export const PROJECT_COPY: string = "PROJECT_COPY"
export const PROJECT_DELETE: string = "PROJECT_DELETE"
export const PROJECT_LOAD: string = "PROJECT_LOAD"
export const PROJECT_ADD_ATTACHMENT: string = "PROJECT_ADD_ATTACHMENT"
export const PROJECT_UPDATE_ATTACHMENT: string = "PROJECT_UPDATE_ATTACHMENT"
export const PROJECT_REMOVE_ATTACHMENT: string = "PROJECT_REMOVE_ATTACHMENT"
export const PROJECT_UPDATE_ATTACHMENTS: string = "PROJECT_UPDATE_ATTACHMENTS"
export const PROJECT_UNDO: string = "PROJECT_UNDO"
export const PROJECT_REDO: string = "PROJECT_REDO"
export const PROJECT_CLEAR_HISTORY: string = "PROJECT_CLEAR_HISTORY"

export function projectClearState(): AppThunkAction<void, ProjectAction> {
  return (dispatch: AppThunkDispatch<ProjectAction>) => {
    dispatch({ type: PROJECT_CLEAR })
  }
}

export function projectFetch(projectId: string): AppThunkAction<void, ProjectAction> {
  return async (dispatch: AppThunkDispatch<ProjectAction>) => {
    const project: Project = await getProject(projectId)
    dispatch(loadProject(project))
  }
}

export function projectLoadFromMap(map: MapDetailDto): AppThunkAction<void, ProjectAction> {
  return (dispatch: AppThunkDispatch<ProjectAction>) => {
    const project = createEmptyProjectFromMap(map)
    dispatch(loadProject(project))
  }
}

export function projectAddDrawing(drawing: IMapDrawing): AppThunkAction<void, ProjectActions> {
  return (dispatch: AppThunkDispatch<ProjectActions>) => {
    dispatch({
      type: PROJECT_ADD_DRAWING,
      payload: drawing
    })

    dispatch(projectSyncPopupStates(drawing))
  }
}

export function projectRemoveDrawing(drawing: IMapDrawing): ProjectDrawAction {
  return {
    type: PROJECT_REMOVE_DRAWING,
    payload: drawing
  }
}

export function projectEditDrawingColor(drawing: IMapDrawing): AppThunkAction<void, ProjectActions> {
  return (dispatch: AppThunkDispatch<ProjectActions>) => {
    dispatch({
      type: PROJECT_EDIT_DRAWING_COLOR,
      payload: drawing
    })

    dispatch(projectSyncPopupStates(drawing))
  }
}

export function projectEditDrawingWeight(drawing: IMapDrawing): AppThunkAction<void, ProjectActions> {
  return (dispatch: AppThunkDispatch<ProjectActions>) => {
    dispatch({
      type: PROJECT_EDIT_DRAWING_WEIGHT,
      payload: drawing
    })

    dispatch(projectSyncPopupStates(drawing))
  }
}

export function projectMoveDrawing(drawing: IMapDrawing): AppThunkAction<void, ProjectActions> {
  return (dispatch: AppThunkDispatch<ProjectActions>) => {
    dispatch({
      type: PROJECT_MOVE_DRAWING,
      payload: drawing
    })

    dispatch(projectSyncPopupStates(drawing))
  }
}

export function projectEditDrawingPopup(drawing: IMapDrawing): AppThunkAction<void, ProjectActions> {
  return (dispatch: AppThunkDispatch<ProjectActions>) => {
    dispatch({
      type: PROJECT_EDIT_DRAWING_POPUP,
      payload: drawing
    })

    dispatch(projectSyncPopupStates(drawing))
  }
}

export function projectCloseDrawingPopup(): AppThunkAction<void, ProjectActions> {
  return (dispatch: AppThunkDispatch<ProjectActions>) => {
    dispatch({
      type: PROJECT_CLOSE_DRAWING_POPUP
    })
  }
}

export function projectAddPointToPolyline(drawing: IMapDrawing): AppThunkAction<void, ProjectActions> {
  return (dispatch: AppThunkDispatch<ProjectActions>) => {
    dispatch({
      type: PROJECT_ADD_POINT_TO_POLYLINE,
      payload: drawing
    })

    dispatch(projectSyncPopupStates(drawing))
  }
}

export function projectEditDrawingText(drawing: IMapDrawing): AppThunkAction<void, ProjectActions> {
  return (dispatch: AppThunkDispatch<ProjectActions>) => {
    dispatch({
      type: PROJECT_EDIT_DRAWING_TEXT,
      payload: drawing
    })

    dispatch(projectSyncPopupStates(drawing))
  }
}

export function projectSyncPopupStates(drawing: IMapDrawing): ProjectDrawingsActions {
  let syncedDrawings: IMapDrawing[] = bosAtlasStore.getState().project.present.project!.drawings

  if (drawing.isPopupOpen) {
    syncedDrawings = produce({ drawings: syncedDrawings }, (draft: { drawings: IMapDrawing[] }) => {
      draft.drawings = draft.drawings.map((draftDrawing: IMapDrawing) => {
        if (draftDrawing.id === drawing.id) {
          return drawing
        }
        return {
          ...draftDrawing,
          isPopupOpen: false
        }
      })
    }).drawings
  }

  return { type: PROJECT_SYNC_POPUP_STATES, payload: syncedDrawings }
}

export function projectCreate(
  project: Project,
  brandName: string,
  mapId?: string
): AppThunkAction<void, ProjectAction> {
  return async (dispatch: AppThunkDispatch<ProjectAction>) => {
    dispatch({
      type: PROJECT_CREATE
    })

    const projectId: string = await createProject(project, brandName)
    const newProject: Project = await getProject(projectId)

    if (mapId) history.replace(`/map/${mapId}/comparison`)
    else window.history.replaceState(null, "", `/atlas/project/${projectId}`)

    dispatch(loadProject(newProject))
  }
}

export function projectUpdate(project: Project, mapId?: string): AppThunkAction<void, ProjectAction> {
  return async (dispatch: AppThunkDispatch<ProjectAction>) => {
    dispatch({
      type: PROJECT_UPDATE
    })

    await updateProject(project)

    dispatch(loadProject(project))

    if (mapId) history.replace(`/map/${mapId}/comparison`)
  }
}

export function projectCopy(project: Project, brandName: string): AppThunkAction<void, ProjectAction> {
  return async (dispatch: AppThunkDispatch<ProjectAction>) => {
    dispatch({
      type: PROJECT_COPY
    })
    const projectId: string = await copyProject(project, brandName)
    const newProject: Project = await getProject(projectId)

    history.replace(`/project/${projectId}`)

    dispatch(loadProject(newProject))
  }
}

export function projectAddAttachment(
  attachment: Attachment,
  file?: File
): AppThunkAction<void, ProjectAttachementAction> {
  return async (dispatch: AppThunkDispatch<ProjectAttachementAction>) => {
    if (file) {
      const uploadResult = upload("file", file)
      attachment.location = (await uploadResult).data
    }
    dispatch({
      type: PROJECT_ADD_ATTACHMENT,
      payload: attachment
    })
  }
}

export function projectRemoveAttachment(attachment: Attachment): AppThunkAction<void, ProjectAttachementAction> {
  return async (dispatch: AppThunkDispatch<ProjectAttachementAction>) => {
    if (isCloudinaryAsset(attachment.location)) {
      await remove(
        "attachment",
        `file/${AttachmentType[attachment.type]}/${extractCloudinaryAssetId(attachment.location!)}`
      )
    }
    dispatch({ type: PROJECT_REMOVE_ATTACHMENT, payload: attachment })
  }
}

export function projectUpdateAttachment(
  attachment: Attachment,
  file?: File
): AppThunkAction<void, ProjectAttachementAction> {
  return async (dispatch: AppThunkDispatch<ProjectAttachementAction>) => {
    if (file) {
      const uploadResult = await upload("file", file)
      attachment.location = uploadResult.data
    }
    dispatch({
      type: PROJECT_UPDATE_ATTACHMENT,
      payload: attachment
    })
  }
}

export function projectDelete(id: string): AppThunkAction<void, ProjectAction> {
  return async (dispatch: AppThunkDispatch<ProjectAction>) => {
    dispatch({
      type: PROJECT_DELETE
    })
    await removeProject(id)

    dispatch(fetchBosatlas())
  }
}

export function projectUpdateAttachments(attachments: Attachment[]): ProjectAttachmentListAction {
  return {
    type: PROJECT_UPDATE_ATTACHMENTS,
    payload: attachments.map((attachment: Attachment, index: number) => {
      return produce(attachment, (draft: Attachment) => {
        draft.order = index + 1
      })
    })
  }
}

export function projectMoveAttachment(attachment: Attachment): AppThunkAction<void, AttachmentEditorAction> {
  return (dispatch: AppThunkDispatch<AttachmentEditorAction | ProjectAttachementAction>) => {
    dispatch({
      type: ATTACHMENT_EDITOR_UPDATE_PIN_POSITION,
      payload: attachment
    })
    dispatch(projectUpdateAttachment(attachment))
  }
}

export function loadProject(project: Project): ProjectAction {
  return {
    type: PROJECT_LOAD,
    payload: project
  }
}

export function projectUndo(): ProjectAction {
  setTimeout(() => window.dispatchEvent(new Event("undo")), 10)

  return { type: PROJECT_UNDO }
}

export function projectRedo(): ProjectAction {
  setTimeout(() => window.dispatchEvent(new Event("redo")), 10)

  return { type: PROJECT_REDO }
}

export function projectClearHistory(): ProjectAction {
  return { type: PROJECT_CLEAR_HISTORY }
}
